<script setup lang="ts">
import {computed} from 'vue'
import {ChevronsUpDown, LogOut, Settings, ShieldCheck, UserRound} from 'lucide-vue-next'
import {useRouter} from 'vue-router'
import {SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {useUserStore} from '@/stores/user'

const props = defineProps<{email: string; isAdmin: boolean}>()
const router = useRouter()
const userStore = useUserStore()
const {isMobile} = useSidebar()
const emit = defineEmits<{settings: []; close: []}>()

const emailParts = computed(() => props.email.split('@'))
const displayName = computed(() => emailParts.value[0] || props.email)
const handle = computed(() => (emailParts.value[1] ? `@${emailParts.value[1]}` : ''))
const initials = computed(() => displayName.value.slice(0, 2).toUpperCase())

function openSettings() {
  emit('settings')
}

function openProfile() {
  router.push('/user')
  emit('close')
}

function openControlPanel() {
  router.push('/cp')
  emit('close')
}
</script>

<template>
  <SidebarMenu>
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <SidebarMenuButton
            size="lg"
            class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            data-testid="user-menu-trigger"
            aria-label="Settings and user menu">
            <div
              class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-xs font-semibold text-sidebar-primary-foreground">
              {{ initials }}
            </div>
            <div class="grid min-w-0 flex-1 text-left text-sm leading-tight">
              <span class="truncate font-medium">{{ displayName }}</span>
              <span class="truncate text-xs text-muted-foreground">{{ handle }}</span>
            </div>
            <ChevronsUpDown class="ml-auto size-4 shrink-0" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          class="w-[var(--reka-dropdown-menu-trigger-width)] min-w-64 rounded-lg"
          :side="isMobile ? 'bottom' : 'top'"
          align="end"
          :side-offset="4">
          <DropdownMenuLabel class="p-0 font-normal">
            <div class="flex items-center gap-3 px-3 py-3 text-left">
              <div
                class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sm font-semibold text-sidebar-primary-foreground">
                {{ initials }}
              </div>
              <div class="grid min-w-0 flex-1 text-left text-sm leading-tight">
                <span class="truncate font-medium">{{ displayName }}</span>
                <span class="truncate text-muted-foreground">{{ handle }}</span>
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuItem data-testid="user-menu-settings" @click="openSettings">
            <Settings class="mr-2 size-4" />
            {{ $t('common.settings') }}
          </DropdownMenuItem>
          <DropdownMenuItem @click="openProfile">
            <UserRound class="mr-2 size-4" />
            {{ $t('common.profile') }}
          </DropdownMenuItem>
          <DropdownMenuItem v-if="props.isAdmin" data-testid="user-menu-control-panel" @click="openControlPanel">
            <ShieldCheck class="mr-2 size-4" />
            {{ $t('common.controlPanel') }}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="user-menu-sign-out" @click="userStore.signOut">
            <LogOut class="mr-2 size-4" />
            {{ $t('common.signOut') }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  </SidebarMenu>
</template>
